'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { OperatorProfile } from '@/lib/types';

interface AuthContextValue {
  operator: OperatorProfile | null;
  session: any;
  isLoading: boolean;
  isAuthenticated: boolean;
  authError: string | null;
  currentOperatorId: string | null;
  setCurrentOperatorId: (id: string) => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const STORAGE_KEY = 'lcm_current_operator_id';

const AuthContext = createContext<AuthContextValue>({
  operator: null,
  session: null,
  isLoading: true,
  isAuthenticated: false,
  authError: null,
  currentOperatorId: null,
  setCurrentOperatorId: () => {},
  logout: async () => {},
  refreshSession: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentOperatorId, setCurrentOperatorIdState] = useState<string | null>(null);
  const [operator, setOperator] = useState<OperatorProfile | null>(null);
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const supabase = createClient();

  // Use a ref to track the user ID that init() already loaded a profile for.
  // This prevents onAuthStateChange SIGNED_IN from firing a redundant second
  // fetchOperatorProfile when the page first loads with an existing session.
  const initLoadedUidRef = useRef<string | null>(null);

  const fetchOperatorProfile = useCallback(async (uid: string, accessToken?: string) => {
    try {
      const headers: Record<string, string> = {};
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }

      const r = await fetch('/api/operators/me', { headers });
      if (!r.ok) {
        const errData = await r.json().catch(() => ({}));
        throw new Error(errData.error ?? `HTTP ${r.status}`);
      }
      const json = await r.json();
      const op: OperatorProfile | null = json.data ?? null;
      setOperator(op);
      setAuthError(null);
      if (op) localStorage.setItem(STORAGE_KEY, op.id);
    } catch (err: any) {
      console.error('[AuthContext] fetchOperatorProfile failed:', err.message);
      setOperator(null);
      // Don't set authError — user is authenticated, just missing an operator profile
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        // getUser() validates the JWT server-side and triggers a silent refresh
        // if the access token is expired. Never use getSession() for this check.
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (!mounted) return;

        if (userError) {
          // "Auth session missing!" is the normal state on the login page —
          // it means no session exists yet, not a real error.
          if (userError.message !== 'Auth session missing!') {
            console.error('[AuthContext] getUser error:', userError.message);
            setAuthError('Errore nel recupero della sessione');
          }
          setSession(null);
          setOperator(null);
          setCurrentOperatorIdState(null);
          return;
        }

        if (user) {
          // Read the (potentially refreshed) session to get the access token
          const { data: { session: s } } = await supabase.auth.getSession();
          setSession(s);
          const uid = user.id;
          localStorage.setItem(STORAGE_KEY, uid);
          setCurrentOperatorIdState(uid);
          // Record which UID init() loaded so onAuthStateChange can skip the duplicate
          initLoadedUidRef.current = uid;
          await fetchOperatorProfile(uid, s?.access_token);
        } else {
          setSession(null);
          setOperator(null);
          setCurrentOperatorIdState(null);
        }
      } catch (err: any) {
        console.error('[AuthContext] init error:', err.message);
        if (mounted) {
          setSession(null);
          setOperator(null);
          setCurrentOperatorIdState(null);
          setAuthError('Errore di inizializzazione autenticazione');
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    init();

    // Listen for auth state changes (sign in, sign out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, s) => {
      if (!mounted) return;

      if (event === 'SIGNED_OUT' || !s?.user) {
        setSession(null);
        setOperator(null);
        setCurrentOperatorIdState(null);
        setAuthError(null);
        initLoadedUidRef.current = null;
        setIsLoading(false);
        return;
      }

      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        // Just update the session token — no need to re-fetch the profile
        setSession(s);
        setAuthError(null);
        setCurrentOperatorIdState(s.user.id);
        localStorage.setItem(STORAGE_KEY, s.user.id);
        return;
      }

      if (event === 'SIGNED_IN') {
        setSession(s);
        setAuthError(null);
        const uid = s.user.id;
        localStorage.setItem(STORAGE_KEY, uid);
        setCurrentOperatorIdState(uid);

        // If init() already fetched the profile for this exact user (existing session
        // on page load), skip the redundant fetch. Clear the ref so subsequent
        // sign-ins (after sign-out) always fetch the profile.
        if (initLoadedUidRef.current === uid) {
          initLoadedUidRef.current = null;
          return;
        }

        setIsLoading(true);
        await fetchOperatorProfile(uid, s.access_token);
        if (mounted) setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('[AuthContext] refreshSession error:', error.message);
        setAuthError('Errore nel rinnovo della sessione');
        return;
      }
      if (user) {
        setAuthError(null);
        setCurrentOperatorIdState(user.id);
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        await fetchOperatorProfile(user.id, currentSession?.access_token);
      }
    } catch (err: any) {
      console.error('[AuthContext] refreshSession exception:', err.message);
      setAuthError('Errore nel rinnovo della sessione');
    }
  }, [fetchOperatorProfile]);

  const setCurrentOperatorId = (id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setCurrentOperatorIdState(id);
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err: any) {
      console.error('[AuthContext] logout error:', err.message);
    } finally {
      localStorage.removeItem(STORAGE_KEY);
      setCurrentOperatorIdState(null);
      setOperator(null);
      setSession(null);
      setAuthError(null);
      initLoadedUidRef.current = null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        operator,
        session,
        isLoading,
        // isAuthenticated is true only when we have a live session with a user.
        // Relying on `operator` alone would cause false positives during sign-out.
        isAuthenticated: !!session?.user,
        authError,
        currentOperatorId,
        setCurrentOperatorId,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { AuthContext };